import { keepPreviousData, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { endpoints, type InvoiceListResponse } from "@/lib/endpoints";
import { queryKeys } from "@/lib/query-keys";

/**
 * The invoice register's data and its filters (§7, §11.2).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE URL IS THE FILTER STATE. Not `useState`, and the reason is not tidiness:
 * the home screen ALREADY links into this screen with real filters on it —
 * `routes.invoicesInPeriod(from, to)` produces `/invoices?from=&to=`, and the
 * chart's twelve month buttons all land here. If the filters lived in component
 * state those links would open an unfiltered register showing every bill ever,
 * and the owner would be looking at the wrong number one tap after tapping the
 * right one.
 *
 * Keeping them in the URL also means the back button undoes a filter, a filtered
 * register can be sent to the son over WhatsApp, and a reload does not silently
 * widen what is on screen.
 *
 * EVERY PARAM NAME MATCHES THE API'S OWN. `status`, `customerId`, `from`, `to`,
 * `issuedManually`, `limit`, `offset` — the exact keys of
 * `InvoiceListQuerySchema`. The schema is `.strict()`, so a name invented here
 * would be a 400 rather than an ignored parameter. That also makes the browser
 * URL and the request query the same thing, which is one fewer mapping to keep
 * true.
 *
 * THERE IS NO TEXT SEARCH, and that is the API's shape, not an omission: the
 * schema has no `q`. "Find Lupin's bills" is `customerId`; "find the bills from
 * August" is `from`/`to`. A search box here would either 400 or have to filter
 * only the current page, which looks like search and silently misses everything
 * on the pages it has not fetched.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** The API's default and hard ceiling — `InvoiceListQuerySchema`, `.max(200)`. */
export const INVOICE_PAGE_SIZE = 50;

/** The columns the API will sort by. A closed set — see the route's schema. */
export type InvoiceSortKey = "number" | "date" | "customer" | "status" | "total";

const INVOICE_SORT_KEYS: readonly InvoiceSortKey[] = [
  "number",
  "date",
  "customer",
  "status",
  "total",
];

export interface InvoiceFilters {
  /**
   * ONE OR SEVERAL. The API accepts `?status=ISSUED,DRAFT` as well as a single
   * value; the URL carries the comma-separated form, which is what survives
   * being copied out of the address bar.
   */
  status?: string;
  customerId?: number;
  /**
   * Free text over the document's OWN reference numbers — invoice number, S.O.,
   * challan, quotation ref. NOT the customer name: that lives in a text-backed
   * JSON column the server will not query inside (CLAUDE.md). Finding a
   * customer's bills is `customerId`.
   */
  q?: string;
  /** `YYYY-MM-DD`, INCLUSIVE — the API compares `gte` / `lte` on `invoice_date`. */
  from?: string;
  to?: string;
  /** INTEGER PAISE, inclusive. `30000000` is ₹3,00,000. */
  minTotalPaise?: number;
  maxTotalPaise?: number;
  issuedManually?: boolean;
  /**
   * Column sorting (§11.2 amendment). In the URL like every other filter, so a
   * sorted register is linkable and survives a refresh — the register is a
   * screen the owner sends his son a link to.
   */
  sort?: InvoiceSortKey;
  dir?: "asc" | "desc";
  offset: number;
}

/** Read the filters out of the URL, coercing exactly as the API would. */
export function useInvoiceFilters(): {
  filters: InvoiceFilters;
  /** Merge a change; any change except paging returns to the first page. */
  setFilters: (patch: Partial<InvoiceFilters>) => void;
  clearFilters: () => void;
  /** How many filters are on, for the "More filters (2)" badge. */
  activeCount: number;
} {
  const [params, setParams] = useSearchParams();

  const filters = useMemo<InvoiceFilters>(() => {
    const status = params.get("status") ?? undefined;
    const rawCustomer = params.get("customerId");
    const customerId =
      rawCustomer === null || rawCustomer === "" ? undefined : Number(rawCustomer);
    const rawManual = params.get("issuedManually");
    const rawOffset = params.get("offset");
    const rawMin = params.get("minTotalPaise");
    const rawMax = params.get("maxTotalPaise");
    const q = params.get("q") ?? undefined;

    /** A bound that is not a whole number is no filter — never send `NaN`. */
    const paise = (raw: string | null): number | undefined => {
      if (raw === null || raw.trim() === "") return undefined;
      const value = Number(raw);
      return Number.isSafeInteger(value) && value >= 0 ? value : undefined;
    };

    return {
      status: status === "" ? undefined : status,
      q: q === "" ? undefined : q,
      minTotalPaise: paise(rawMin),
      maxTotalPaise: paise(rawMax),
      // A junk `?customerId=abc` becomes "no filter" rather than `NaN`, which
      // would serialise into the request and earn a 400 the owner cannot read.
      customerId: customerId === undefined || !Number.isFinite(customerId) ? undefined : customerId,
      from: params.get("from") ?? undefined,
      to: params.get("to") ?? undefined,
      issuedManually: rawManual === null || rawManual === "" ? undefined : rawManual === "true",
      // A hand-edited `?sort=whatever` is no sort rather than a 400 — the URL
      // is user-facing and a typo in it should degrade, not break.
      sort: INVOICE_SORT_KEYS.find((key) => key === params.get("sort")),
      dir: params.get("dir") === "asc" ? "asc" : params.get("dir") === "desc" ? "desc" : undefined,
      offset: rawOffset === null ? 0 : Math.max(0, Number(rawOffset) || 0),
    };
  }, [params]);

  const setFilters = useCallback(
    (patch: Partial<InvoiceFilters>) => {
      const next = new URLSearchParams(params);

      for (const [key, value] of Object.entries(patch)) {
        if (value === undefined || value === "") next.delete(key);
        else next.set(key, String(value));
      }

      /*
       * ANY FILTER CHANGE RETURNS TO PAGE ONE. Narrowing a 214-row register to
       * 3 rows while still sitting at offset 150 shows an empty page, which
       * reads as "no bills match" when in fact three do.
       */
      if (!("offset" in patch)) next.delete("offset");

      // `replace` so a filter fiddle does not stack twenty history entries
      // between the owner and the screen he came from.
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const clearFilters = useCallback(() => {
    setParams(new URLSearchParams(), { replace: true });
  }, [setParams]);

  const activeCount = [
    filters.status,
    filters.customerId,
    filters.q,
    // A range counts once, however many ends it has — "2 filters" for one date
    // range would make the badge read as more than is actually on.
    filters.from ?? filters.to,
    filters.minTotalPaise ?? filters.maxTotalPaise,
    filters.issuedManually,
  ].filter((value) => value !== undefined).length;

  return { filters, setFilters, clearFilters, activeCount };
}

/** The filters as the API wants them — omitting every key that is not set. */
function toQuery(filters: InvoiceFilters): Record<string, string | number | boolean | undefined> {
  return {
    status: filters.status,
    customerId: filters.customerId,
    q: filters.q,
    from: filters.from,
    to: filters.to,
    minTotalPaise: filters.minTotalPaise,
    maxTotalPaise: filters.maxTotalPaise,
    issuedManually: filters.issuedManually,
    sort: filters.sort,
    dir: filters.dir,
    limit: INVOICE_PAGE_SIZE,
    offset: filters.offset === 0 ? undefined : filters.offset,
  };
}

/**
 * `enabled: false` for the register's §17.5 UNPAID mode, and it is not a
 * micro-optimisation.
 *
 * "Still owed" is `grand_total_paise` − `SUM(payments.amount_paise)`, which this
 * route's `where` cannot express (Prisma will not filter on an aggregate of a
 * related table), so that mode is answered by `GET /payments/outstanding`
 * instead — a different request, not a narrower one. While it is showing, this
 * one would fetch the first fifty bills of the whole book to display none of
 * them, over the mobile data of a workshop.
 */
export function useInvoiceList(
  filters: InvoiceFilters,
  options: { enabled?: boolean } = {},
): UseQueryResult<InvoiceListResponse> {
  const query = toQuery(filters);

  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) search.set(key, String(value));
  }
  const qs = search.toString();

  return useQuery({
    // The filter bag is part of the key BY VALUE, so paging back to a page
    // already fetched paints from cache instead of re-hitting the network.
    queryKey: queryKeys.invoices.list(query),
    queryFn: () => api.get<InvoiceListResponse>(`${endpoints.invoices}${qs === "" ? "" : `?${qs}`}`),
    enabled: options.enabled ?? true,
    /*
     * The previous page stays on screen while the next one loads, instead of the
     * table collapsing to a spinner and the page height jumping. On a slow
     * connection that flicker is what makes paging feel broken.
     */
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}
